import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Filter, X, ArrowUp } from 'lucide-react';
import { Reveal, SectionEyebrow } from '../components/ui-bits';
import { getClients } from '../lib/api';
import Seo from '../components/Seo';

const _surname = (name) => {
  const parts = name.trim().split(/\s+/);
  return (parts[parts.length - 1] || name).toLowerCase();
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [filters, setFilters] = useState({ accent: '', skill: '', hair: '', eye: '' });
  const [open, setOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const [flashLetter, setFlashLetter] = useState(null);
  const letterRefs = useRef({});

  useEffect(() => { getClients().then(setClients).catch(() => {}); }, []);

  // Back-to-top visibility
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 800);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const options = useMemo(() => {
    const set = (key) => Array.from(new Set(clients.flatMap(c => (Array.isArray(c[key]) ? c[key] : [c[key]])))).filter(Boolean);
    return {
      accent: set('accents'),
      skill: set('skills'),
      hair: set('hair_colour'),
      eye: set('eye_colour'),
    };
  }, [clients]);

  const filtered = useMemo(() => clients.filter(c => {
    if (filters.accent && !c.accents.includes(filters.accent)) return false;
    if (filters.skill && !c.skills.includes(filters.skill)) return false;
    if (filters.hair && c.hair_colour !== filters.hair) return false;
    if (filters.eye && c.eye_colour !== filters.eye) return false;
    return true;
  }), [clients, filters]);

  const clearAll = () => setFilters({ accent: '', skill: '', hair: '', eye: '' });
  const activeCount = Object.values(filters).filter(Boolean).length;

  // A-Z index — set of surname letters that actually have actors in the filtered list.
  const letterCounts = useMemo(() => {
    const counts = {};
    filtered.forEach((c) => {
      const l = _surname(c.name).charAt(0).toUpperCase();
      counts[l] = (counts[l] || 0) + 1;
    });
    return counts;
  }, [filtered]);

  const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const scrollToLetter = (letter) => {
    const node = letterRefs.current[letter];
    if (!node) return;
    const y = node.getBoundingClientRect().top + window.pageYOffset - 100;
    window.scrollTo({ top: y, behavior: 'smooth' });
    setFlashLetter(letter);
    setTimeout(() => setFlashLetter(null), 800);
  };

  // Keyboard shortcut: pressing A-Z jumps to that section.
  useEffect(() => {
    const onKey = (e) => {
      if (open) return;                                     // ignore while drawer open
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target && e.target.tagName) || '';
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;
      const key = e.key.toUpperCase();
      if (key.length === 1 && key >= 'A' && key <= 'Z' && letterRefs.current[key]) {
        e.preventDefault();
        scrollToLetter(key);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, clients, filters]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Group filtered clients by first letter of surname so we can insert anchors.
  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((c) => {
      const l = _surname(c.name).charAt(0).toUpperCase();
      if (!map.has(l)) map.set(l, []);
      map.get(l).push(c);
    });
    return Array.from(map.entries());
  }, [filtered]);

  return (
    <div data-testid="clients-page" className="bg-white">
      <Seo
        title="Our Clients"
        description="The David Ball Management client list — a considered group of actors working across television, film, theatre, commercials and voice. Browse the full A-Z."
        path="/clients"
      />
      {/* Hero */}
      <section className="pt-40 pb-16 lg:pt-48 lg:pb-24 bg-[#faf8f4]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
          <Reveal>
            <SectionEyebrow>Our Clients</SectionEyebrow>
          </Reveal>
          <div className="grid lg:grid-cols-12 gap-12 mt-10 items-end">
            <Reveal className="lg:col-span-8" delay={120}>
              <h1 className="font-heading uppercase text-[#002b5c] text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9]">
                A considered<br /><span className="italic font-light">client list.</span>
              </h1>
            </Reveal>
            <Reveal className="lg:col-span-4" delay={240}>
              <p className="text-base text-[#4a4a4a] leading-relaxed">
                David Ball Management represents a small, deliberately curated group of actors, working across
                television, film, theatre, commercials and voice.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Filters + Grid */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="flex items-center justify-between mb-8">
          <p className="text-[11px] tracking-[0.24em] uppercase text-[#6b6b6b]">
            <span data-testid="clients-count" className="text-[#002b5c] font-medium">{filtered.length}</span> actors
          </p>
          <button
            data-testid="clients-filter-toggle"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 rounded-full border border-[#002b5c]/30 px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase text-[#002b5c] hover:bg-[#002b5c] hover:text-white transition-colors"
          >
            <Filter size={14} /> Filter {activeCount > 0 && <span className="ml-1 text-[#C5A059]">· {activeCount}</span>}
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-8">
          {filtered.map((c, i) => (
            <Reveal key={c.id} delay={(i % 8) * 60}>
              <Link
                to={`/clients/${c.slug}`}
                data-testid={`client-card-${c.slug}`}
                className="block group"
              >
                <div className="img-hover rounded-xl overflow-hidden aspect-[3/4] bg-neutral-100">
                  <img src={c.headshot} alt={c.name} className="w-full h-full object-cover" />
                </div>
                <div className="mt-4 flex items-center justify-between gap-3">
                  <h3 className="font-heading text-xl md:text-2xl uppercase text-[#002b5c] leading-tight">{c.name}</h3>
                  <ArrowUpRight size={16} className="text-[#C5A059] shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="py-24 text-center">
            <p className="font-heading text-3xl uppercase text-[#002b5c]">No actors match those filters.</p>
            <button onClick={clearAll} className="mt-6 text-[11px] tracking-[0.24em] uppercase text-[#C5A059] gold-underline">Clear filters</button>
          </div>
        )}
      </section>

      {/* Filter Drawer */}
      <div
        data-testid="clients-filter-drawer"
        className={`fixed inset-0 z-50 transition-opacity duration-500 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      >
        <div className="absolute inset-0 bg-[#001a38]/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
        <aside className={`absolute right-0 top-0 bottom-0 w-full sm:w-[420px] bg-white p-8 overflow-y-auto shadow-2xl transition-transform duration-500 ${open ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex items-center justify-between mb-10">
            <div>
              <p className="eyebrow">Refine</p>
              <h3 className="font-heading text-3xl uppercase text-[#002b5c] mt-2">Filter the client list</h3>
            </div>
            <button aria-label="close" onClick={() => setOpen(false)} className="text-[#002b5c] p-2"><X size={22} /></button>
          </div>

          {[
            { key: 'accent', label: 'Accents' },
            { key: 'skill', label: 'Skills' },
            { key: 'hair', label: 'Hair Colour' },
            { key: 'eye', label: 'Eye Colour' },
          ].map((f) => (
            <div key={f.key} className="mb-8">
              <p className="eyebrow mb-3">{f.label}</p>
              <div className="flex flex-wrap gap-2">
                {options[f.key].map((opt) => {
                  const active = filters[f.key] === opt;
                  return (
                    <button
                      key={opt}
                      data-testid={`filter-${f.key}-${opt}`}
                      onClick={() => setFilters((p) => ({ ...p, [f.key]: active ? '' : opt }))}
                      className={`rounded-full px-4 py-2 text-[11px] tracking-[0.18em] uppercase border transition-all ${
                        active
                          ? 'bg-[#002b5c] text-white border-[#002b5c]'
                          : 'border-black/15 text-[#333] hover:border-[#002b5c]'
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="hairline my-8" />
          <div className="flex items-center gap-3">
            <button onClick={clearAll} className="flex-1 rounded-full border border-[#002b5c]/30 px-5 py-3 text-[11px] tracking-[0.22em] uppercase text-[#002b5c] hover:bg-[#faf8f4] transition-colors">
              Clear
            </button>
            <button onClick={() => setOpen(false)} className="flex-1 rounded-full bg-[#002b5c] text-white px-5 py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-[#001a38] transition-colors">
              View {filtered.length}
            </button>
          </div>
        </aside>
      </div>
      {/* Back to top */}
      <button
        data-testid="clients-back-to-top"
        aria-label="Back to top"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-24 right-8 z-40 w-12 h-12 rounded-full bg-[#002b5c] text-white shadow-2xl border border-[#C5A059]/60 inline-flex items-center justify-center transition-all duration-500 hover:bg-[#001a38] hover:-translate-y-0.5 ${showTop ? 'opacity-100' : 'opacity-0 pointer-events-none translate-y-2'}`}
      >
        <ArrowUp size={18} />
      </button>
    </div>
  );
}
